import { Edge } from "@atlaskit/pragmatic-drag-and-drop-hitbox/types";
import { combine } from "@atlaskit/pragmatic-drag-and-drop/combine";
import { dropTargetForElements } from "@atlaskit/pragmatic-drag-and-drop/element/adapter";
import React, { useEffect, useRef, useState } from "react";

import { DragData, TicketGroup } from "../../containers/Tickets/types";
import { useDispatchMove } from "./useDispatchMove";

interface UseColumnDropProps {
  group: TicketGroup;
  dropzoneRef: React.RefObject<HTMLDivElement>;
  subGroupId?: string | null;
  ticketsLength?: number;
}

export function useColumnDrop({
  group,
  dropzoneRef,
  subGroupId,
  ticketsLength,
}: UseColumnDropProps) {
  const dispatchMove = useDispatchMove();
  const [isDraggingOver, setIsDraggingOver] = useState(false);
  const [dropEdge, setDropEdge] = useState<Edge | null>(null);
  const [draggingCardHeight, setDraggingCardHeight] = useState(0);
  const [closestTicketId, setClosestTicketId] = useState<string | null>(null);

  const destLength = ticketsLength ?? group?.ticketsList?.length;
  const closestTicketIdRef = useRef<string | null>(null);
  const dropEdgeRef = useRef<Edge | null>(null);

  useEffect(() => {
    if (!dropzoneRef.current) return;

    return combine(
      dropTargetForElements({
        element: dropzoneRef.current,
        canDrop: ({ source }) => {
          const data = source.data as unknown as DragData;
          return true;
        },

        onDrag: ({ source, location }) => {
          const data = source.data as unknown as DragData;
          const isSameGroup = data.status === group._id;
          const normalizedSource = data.subGroupId ?? null;
          const normalizedTarget = subGroupId ?? null;
          const isSameSubGroup = normalizedSource === normalizedTarget;

          const draggedEl = document.querySelector(
            `[data-ticket-id="${data.ticketId}"]`,
          ) as HTMLElement;
          if (draggedEl) {
            setDraggingCardHeight(draggedEl.offsetHeight);
          }

          const mouseY = location.current.input.clientY;
          const dropzone = dropzoneRef.current;
          const allCards = dropzone
            ? (dropzone.querySelectorAll(
                `[data-ticket-id]`,
              ) as NodeListOf<HTMLElement>)
            : ([] as unknown as NodeListOf<HTMLElement>);

          let closestCard: HTMLElement | null = null;
          let closestEdge: Edge = "bottom";
          let closestDistance = Infinity;

          allCards.forEach((card) => {
            if (card.dataset.ticketId === data.ticketId) return;

            const rect = card.getBoundingClientRect();
            const cardMidY = rect.top + rect.height / 2;
            const distance = Math.abs(mouseY - cardMidY);

            if (distance < closestDistance) {
              closestDistance = distance;
              closestCard = card;
              closestEdge = mouseY < cardMidY ? "top" : "bottom";
            }
          });

          if (isSameGroup && isSameSubGroup && closestCard && draggedEl) {
            const draggedRect = draggedEl.getBoundingClientRect();
            const closestRect = (
              closestCard as HTMLElement
            ).getBoundingClientRect();

            const draggedIsAbove = draggedRect.bottom <= closestRect.top + 10;
            if (draggedIsAbove && closestEdge !== "bottom") {
              setClosestTicketId(null);
              closestTicketIdRef.current = null; // ✅
              setIsDraggingOver(false);
              return;
            }

            const draggedIsBelow = draggedRect.top >= closestRect.bottom - 10;
            if (draggedIsBelow && closestEdge === "bottom") {
              setClosestTicketId(null);
              closestTicketIdRef.current = null; // ✅
              setIsDraggingOver(false);
              return;
            }
          }

          if (closestCard) {
            const ticketId =
              (closestCard as HTMLElement).dataset.ticketId ?? null;
            setClosestTicketId(ticketId);
            closestTicketIdRef.current = ticketId; // ✅ ref update
          } else {
            setClosestTicketId(null);
            closestTicketIdRef.current = null; // ✅
          }

          setDropEdge(closestEdge);
          dropEdgeRef.current = closestEdge; // ✅ ref update
          setIsDraggingOver(true);
        },

        onDragLeave: () => {
          setIsDraggingOver(false);
          setDropEdge(null);
          setDraggingCardHeight(0);
          setClosestTicketId(null);
          closestTicketIdRef.current = null;
          dropEdgeRef.current = null;
        },

        onDrop: ({ source }) => {
          setIsDraggingOver(false);
          setDropEdge(null);
          setDraggingCardHeight(0);

          const data = source.data as unknown as DragData;
          if (!data?.ticketId) return;

          const isSameGroup = data.status === group._id;
          const normalizedSource = data.subGroupId ?? null;
          const normalizedTarget = subGroupId ?? null;
          const isSameSubGroup = normalizedSource === normalizedTarget;
          if (isSameGroup && isSameSubGroup) {
            closestTicketIdRef.current = null;
            return;
          }

          const currentClosestId = closestTicketIdRef.current;
          const currentDropEdge = dropEdgeRef.current;

          let destinationIndex = destLength;

          const dropzone = dropzoneRef.current;
          if (currentClosestId && dropzone) {
            const allCards = Array.from(
              dropzone.querySelectorAll("[data-ticket-id]"),
            ) as HTMLElement[];

            const closestIndex = allCards.findIndex(
              (card) => card.dataset.ticketId === currentClosestId,
            );

            if (closestIndex !== -1) {
              destinationIndex =
                currentDropEdge === "top" ? closestIndex : closestIndex + 1;
            }
          }

          closestTicketIdRef.current = null;
          dropEdgeRef.current = null;

          dispatchMove(data, group._id, subGroupId ?? null, destinationIndex);
        },
      }),
    );
  }, [group._id, destLength, subGroupId, dropzoneRef]);

  return { isDraggingOver, dropEdge, draggingCardHeight, closestTicketId };
}
